import { useCallback, useRef, useState } from "react";
import axios from "axios";
import { serverRefusalMessage } from "@/lib/serverError";
import { handleCreateModel } from "@/components/main/utils/models/ModelServices";
import { useMainStateStore } from "@/components/main/stores/mainStateStore";
import { useOrgContextStore } from "@/components/main/stores/orgContextStore";
import type { ModelElementType } from "@/components/entities";

/// Where a new model goes — named every time, so nothing lands in a space the
/// caller didn't choose.
export type CreateModelTarget = { spaceId: string } | { catalog: true };

/// What the naming overlay hands over: the trimmed name and the appearance it
/// showed, so the row comes back looking the way the card did.
export interface NewModelDraft {
  name: string;
  color: string;
  icon: number;
}

/// A refusal carries the door's sentence when it wrote one; `null` means the
/// request never got an answer worth showing, so the caller words a retry.
export type CreateModelOutcome =
  { ok: true; model: ModelElementType } | { ok: false; message: string | null };

interface UseCreateModel {
  /// Id of the most recently created model, so the list can play the settle-in
  /// `overview-fresh` animation on that row (`isFresh` on `ModelRow`).
  freshModelId: string | null;
  /// True while a create request is in flight (guards double-submits).
  isCreating: boolean;
  /// A catalog model is role-owned — the server rejects it without
  /// `can_manage_public_catalog`.
  createModel: (
    target: CreateModelTarget,
    draft: NewModelDraft,
  ) => Promise<CreateModelOutcome>;
}

/// The one "create a model" action, shared by the Overview and the Catalog
/// models page: both lists get the row the same way and play the same outline.
/// It never toasts — the naming overlay shows a refusal under the field it is
/// about, with the name still in it.
export function useCreateModel(): UseCreateModel {
  const [freshModelId, setFreshModelId] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const inFlight = useRef(false);
  const addModel = useMainStateStore((s) => s.addModel);

  const createModel = useCallback(
    async (
      target: CreateModelTarget,
      draft: NewModelDraft,
    ): Promise<CreateModelOutcome> => {
      if (inFlight.current) return { ok: false, message: null };
      // The door refuses a scheduled space, and its 409 would surface as the
      // generic failure — say which space and why instead.
      const space =
        "spaceId" in target
          ? useOrgContextStore.getState().spaceById(target.spaceId)
          : undefined;
      if (space?.deletion_scheduled_at) {
        return {
          ok: false,
          message: `${space.name} is scheduled for deletion — it takes no new models until that is cancelled`,
        };
      }
      inFlight.current = true;
      setIsCreating(true);
      try {
        const created = await handleCreateModel({
          ...("spaceId" in target
            ? { space_id: target.spaceId }
            : { is_catalog: true }),
          ...draft,
        });
        addModel(created);
        setFreshModelId(String(created.model_id));
        return { ok: true, model: created };
      } catch (error) {
        const body = axios.isAxiosError(error)
          ? error.response?.data
          : undefined;
        // A quota refusal is news about the org; the banner must not lag it.
        const refusal = serverRefusalMessage(body);
        if (refusal) void useOrgContextStore.getState().refreshUsage();
        const plain =
          typeof body === "string" &&
          body.trim() &&
          !body.trim().startsWith("{")
            ? body.trim()
            : null;
        return { ok: false, message: refusal ?? plain };
      } finally {
        inFlight.current = false;
        setIsCreating(false);
      }
    },
    [addModel],
  );

  return { freshModelId, isCreating, createModel };
}
